import React, { useState } from "react";
import { Editor, EditorState, CompositeDecorator } from "draft-js";
import AutocompleteSpanComponent from './AutoComplete/AutocompleteSpan';
import { findAutocompleteText, handleBeforeInput, handleReturn, handlePastedText } from './AutoComplete/utils/autocompleteUtils';
import './MyEditor.css';
import SuggestionsList from './SuggestionsDropdown/SuggestionsList';
import { useAutocomplete } from './AutoComplete/hooks/useAutocomplete';
import { useSuggestionList } from './SuggestionsDropdown/hooks/useSuggestionList';
import { useEditorChange } from './hooks/useEditorChange';


/**
 * Available suggestions for autocomplete
 */
const suggestions: string[] = ["apple", "apples in the basket", "banana", "cherry", "date", "elderberry", "fig", "grape", "honeydew", "kiwi"];


/**
 * Draft.js decorator configuration for styling autocompleted text
 */
const decorator: CompositeDecorator = new CompositeDecorator([
  {
    strategy: findAutocompleteText,
    component: AutocompleteSpanComponent,
  },
]);


/**
 * Rich text editor with autocomplete functionality.
 * Typing '<>' triggers a suggestions dropdown.
 * Arrow keys navigate suggestions, Enter/Tab to select.
 */
const MyEditor: React.FC = () => {
  // Initialize editor state with focus at the end
  const [editorState, setEditorState] = useState<EditorState>(
    EditorState.moveFocusToEnd(EditorState.createEmpty(decorator))
  );
 
  // Track filtered suggestions based on user input
  const [filteredSuggestions, setFilteredSuggestions] = useState<readonly string[]>(suggestions);


  // Hook for handling suggestion selection and insertion
  const { handleSuggestionSelect } = useAutocomplete();


  // Hook for managing suggestions dropdown state and keyboard navigation
  const {
    dropdownVisible,
    setDropdownVisible,
    selectedIndex,
    setSelectedIndex,
    handleKeyCommand,
    keyBindingFn,
    selectSuggestion
  } = useSuggestionList({
    filteredSuggestions,
    onSuggestionSelect: (suggestion) => handleSuggestionSelect(suggestion, editorState, setEditorState)
  });


  // Hook for handling editor content changes and filtering suggestions
  const handleChange = useEditorChange({
    setEditorState,
    setDropdownVisible,
    setFilteredSuggestions,
    setSelectedIndex,
    dropdownVisible,
    filteredSuggestions,
    selectedIndex,
    allSuggestions: suggestions
  });
 
  return (
    <div className="editor-container">
      <h1>Type normally, and type &lt;&gt; for auto complete suggestions.</h1>
      <Editor
        editorState={editorState}
        onChange={handleChange}
        handlePastedText={handlePastedText}
        handleBeforeInput={handleBeforeInput}
        handleReturn={handleReturn}
        handleKeyCommand={handleKeyCommand}
        keyBindingFn={keyBindingFn}
      />
      <SuggestionsList
        suggestions={filteredSuggestions}
        selectedIndex={selectedIndex}
        onSelect={selectSuggestion}
        visible={dropdownVisible}
      />
    </div>
  );
};


export default MyEditor;